import React, {
  useEffect, useRef, useState,
} from 'react';
import { usePopper } from 'react-popper';
import PropTypes from 'prop-types';
import './popper.scss';
import clsx from 'clsx';
import { CSSTransition } from 'react-transition-group';
import { getUUID } from '../../../utils/general-utils';

const PopperPlacements = {
  TOP: 'top',
  TOP_START: 'top-start',
  TOP_END: 'top-end',
  BOTTOM: 'bottom',
  BOTTOM_START: 'bottom-start',
  BOTTOM_END: 'bottom-end',
  RIGHT: 'right',
  RIGHT_START: 'right-start',
  RIGHT_END: 'right-end',
  LEFT: 'left',
  LEFT_START: 'left-start',
  LEFT_END: 'left-end',
};

const PopperTriggers = {
  CLICK: 'click',
  HOVER: 'hover',
};

const hoverShowEvents = ['mouseenter', 'focus'];

const hoverHideEvents = ['mouseleave', 'blur'];

const TransitionDuration = 200;

const PopperMinWidth = 200;

const Popper = ({
  view, trigger, placement, children, enterDelay, onClose, open, onOpen,
}) => {
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const popperInstance = useRef(null);
  const enterTimer = useRef();
  const leaveTimer = useRef();
  const uuidMarker = useRef(getUUID());

  useEffect(() => {
    if (open && popperInstance.current.update) {
      popperInstance.current.update();
    }
  }, [open]);

  popperInstance.current = usePopper(referenceElement, popperElement, {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
    ],
    placement,
    strategy: 'fixed',
  });

  const getUUIDMarker = () => uuidMarker.current;

  const handleOpen = () => {
    if (onOpen && typeof onOpen === 'function') {
      onOpen();
    }
  };

  const handleClose = () => {
    if (onClose && typeof onClose === 'function') {
      onClose();
    }
  };

  const handleEnter = (event) => {
    clearTimeout(enterTimer.current);
    clearTimeout(leaveTimer.current);

    if (enterDelay) {
      enterTimer.current = setTimeout(
        () => {
          handleOpen(event);
        },
        enterDelay,
      );
    } else {
      handleOpen(event);
    }
  };

  const handleLeave = (event) => {
    clearTimeout(enterTimer.current);
    clearTimeout(leaveTimer.current);
    handleClose(event);
  };

  const handleReferenceClicked = (event) => {
    if (!open) {
      // eslint-disable-next-line no-param-reassign,no-underscore-dangle
      event._popperSelfClicked_ = getUUIDMarker();
    }
    handleOpen();
  };

  const handlePopperClicked = (event) => {
    // eslint-disable-next-line no-param-reassign,no-underscore-dangle
    event.nativeEvent._popperSelfClicked_ = getUUIDMarker();
  };

  const handleDocumentClick = (event) => {
    if (
      open
      // eslint-disable-next-line no-underscore-dangle
      && event._popperSelfClicked_ !== getUUIDMarker()
    ) {
      handleClose(event);
    }
  };

  useEffect(() => {
    if (referenceElement && popperElement) {
      if (trigger === PopperTriggers.HOVER) {
        hoverShowEvents.forEach((event) => {
          referenceElement.addEventListener(event, handleEnter);
        });

        hoverHideEvents.forEach((event) => {
          referenceElement.addEventListener(event, handleLeave);
        });
      } else if (trigger === PopperTriggers.CLICK) {
        referenceElement.addEventListener('click', handleReferenceClicked);
        document.addEventListener('click', handleDocumentClick);
      }
    }
    return () => {
      if (referenceElement) {
        hoverShowEvents.forEach((event) => {
          referenceElement.removeEventListener(event, handleEnter);
        });

        hoverHideEvents.forEach((event) => {
          referenceElement.removeEventListener(event, handleLeave);
        });

        referenceElement.removeEventListener('click', handleReferenceClicked);
      }
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [referenceElement, popperElement, open]);

  const childrenProps = {
    ...children.props,
    dropdownOpen: open,
    ref: setReferenceElement,
  };

  const getPopperMinWidth = () => Math.max(
    PopperMinWidth,
    referenceElement ? referenceElement.clientWidth : 0,
  );

  const getPopperElement = () => (
    <CSSTransition nodeRef={{ current: popperElement }} in={open} timeout={TransitionDuration} classNames="tp-trans">
      <div
        className={clsx('popper', trigger)}
        ref={setPopperElement}
        style={{ ...popperInstance.current.styles.popper, minWidth: getPopperMinWidth() }}
        data-show={open}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...popperInstance.current.attributes.popper}
        onClick={handlePopperClicked}
      >
        {view}
      </div>
    </CSSTransition>
  );

  return (
    <>
      {React.cloneElement(children, childrenProps)}
      {getPopperElement()}
    </>
  );
};

Popper.propTypes = {
  view: PropTypes.element,
  trigger: PropTypes.oneOf(Object.values(PopperTriggers)),
  placement: PropTypes.oneOf(Object.values(PopperPlacements)),
  children: PropTypes.element.isRequired,
  enterDelay: PropTypes.number,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  open: PropTypes.bool,
};

Popper.defaultProps = {
  view: null,
  trigger: PopperTriggers.CLICK,
  placement: PopperPlacements.BOTTOM,
  enterDelay: 100,
  onClose: () => {},
  onOpen: () => {},
  open: false,
};

export {
  Popper as default,
  PopperPlacements,
  PopperTriggers,
};
